/**
 * counsellingFilters — the `path x role` filter matrix from Angular
 * `DEEComponent.GetAllNewCounselling()`.
 *
 * THIS IS THE HIGHEST-RISK LOGIC IN THE MIGRATION. Every condition is ported
 * verbatim from the Angular source. It decides which counselling events each
 * user role sees for each `path`. A single flipped comparison changes who sees
 * what — do not "simplify".
 *
 * Angular compared loosely (`==`) against a mix of string and number literals.
 * Here every field is read via `str()` (String coercion) and compared against
 * string literals, which reproduces Angular's loose-equality results for the
 * values these fields actually carry ('1','2',... etc.).
 */

import { CounsellingRecord, RoleContext, CounsellingPath } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Normalise any wire value to a string for comparison (mirrors Angular `==`). */
const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

/**
 * Filters used by paths 'vacancy' | 'candidate' | 'order-copy' and the empty
 * (conduct counselling) path use `cnsnStartSts` = records with Status == '1'.
 * The add-candidate/seniority/update-vac paths filter the full list.
 */
export const startStatusList = (
  list: CounsellingRecord[]
): CounsellingRecord[] => list.filter((ftr) => str(ftr.Status) === '1');

/** Common "Status is 2 or 0" predicate reused throughout the matrix. */
const status2or0 = (ftr: CounsellingRecord): boolean =>
  str(ftr.Status) === '2' || str(ftr.Status) === '0';

/**
 * path === 'add-candidate' || path === 'seniority'
 * Uses the FULL filtered list (not cnsnStartSts).
 */
function filterAddCandidateOrSeniority(
  list: CounsellingRecord[],
  role: RoleContext
): CounsellingRecord[] {
  const { userTypeId: u, userType1: u1 } = role;

  if (
    u === '5' &&
    u1 !== '8' &&
    u1 !== '29' &&
    u1 !== '6' &&
    u1 !== '5' &&
    u1 !== '7'
  ) {
    // State
    return list.filter((ftr) => status2or0(ftr) && str(ftr.CanUpdtBy) === '1');
  } else if (u === '9' && u1 !== '29') {
    // CEO
    return list.filter(
      (ftr) =>
        ((str(ftr.Directorate) === '1' ||
          str(ftr.Directorate) === '2' ||
          str(ftr.Directorate) === '3') &&
          status2or0(ftr) &&
          str(ftr.CanUpdtBy) === '2') ||
        ((str(ftr.Directorate) === '1' ||
          str(ftr.Directorate) === '2' ||
          str(ftr.Directorate) === '3') &&
          status2or0(ftr) &&
          (str(ftr.CanUpdtBy) === '2' ||
            str(ftr.CanUpdtBy) === '3' ||
            str(ftr.CanUpdtBy) === '4') &&
          str(ftr.schType) === '2')
    );
  } else if (u === '9' && u1 === '29') {
    // GCC CEO
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '4' &&
        status2or0(ftr) &&
        (str(ftr.CanUpdtBy) === '6' || str(ftr.CounsellingType) === '2')
    );
  } else if (u === '6' && u1 !== '29') {
    // BEO
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '1' &&
        status2or0(ftr) &&
        str(ftr.CanUpdtBy) === '2'
    );
  } else if (u === '10') {
    // DEO
    if (u1 === '2') {
      // DEE
      return list.filter(
        (ftr) =>
          str(ftr.Directorate) === '1' &&
          status2or0(ftr) &&
          str(ftr.CanUpdtBy) === '3'
      );
    } else if (u1 === '3') {
      // DSE
      return list.filter(
        (ftr) =>
          str(ftr.Directorate) === '2' &&
          status2or0(ftr) &&
          str(ftr.CanUpdtBy) === '4'
      );
    }
    return [];
  } else if (u === '6' && u1 === '29') {
    // AEO
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '4' &&
        status2or0(ftr) &&
        (str(ftr.CanUpdtBy) === '6' || str(ftr.CounsellingType) === '2')
    );
  } else if (u === '5' && u1 === '29') {
    // GCC State
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '4' &&
        status2or0(ftr) &&
        str(ftr.CanUpdtBy) === '1'
    );
  } else if ((u === '5' && u1 === '6') || (u === '5' && u1 === '7')) {
    // SCERT State
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '5' &&
        status2or0(ftr) &&
        str(ftr.CanUpdtBy) === '1'
    );
  } else if (u === '22') {
    // DIET
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '5' &&
        status2or0(ftr) &&
        str(ftr.CanUpdtBy) === '7'
    );
  } else if (u === '5' && u1 === '8') {
    // Kallar Schools
    return list.filter(
      (ftr) =>
        str(ftr.Directorate) === '6' &&
        status2or0(ftr) &&
        str(ftr.CanUpdtBy) === '1'
    );
  }
  return [];
}

/** path === 'update-vac' */
function filterUpdateVac(
  list: CounsellingRecord[],
  role: RoleContext
): CounsellingRecord[] {
  const { userTypeId: u, userType1: u1 } = role;

  if (u === '5' && u1 !== '29') {
    return list.filter((ftr) => status2or0(ftr) && str(ftr.VacUpdtBy) === '1');
  } else if (u === '9' && u1 === '29') {
    return list.filter(
      (ftr) =>
        status2or0(ftr) &&
        str(ftr.VacUpdtBy) === '1' &&
        str(ftr.Directorate) === '4'
    );
  }
  return list.filter((ftr) => status2or0(ftr) && str(ftr.VacUpdtBy) === '2');
}

/** path === 'vacancy' || path === 'candidate' (Start Counselling filter) */
function filterVacancyOrCandidate(
  list: CounsellingRecord[],
  role: RoleContext
): CounsellingRecord[] {
  const { userTypeId: u, userType1: u1, isScert } = role;

  if (
    (u === '9' && u1 === '29') ||
    (u === '5' && u1 === '29') ||
    (u === '6' && u1 === '29')
  ) {
    // GCC CEO
    return list.filter((ftr) => str(ftr.Directorate) === '4');
  } else if (u === '5' && u1 === '8') {
    return list.filter((ftr) => str(ftr.Directorate) === '6');
  } else if (isScert) {
    return list.filter((ftr) => str(ftr.Directorate) === '5');
  }
  return list.filter((ftr) => str(ftr.Directorate) !== '4');
}

/** path === 'order-copy' (operates on cnsnStartSts = Status == '1') */
function filterOrderCopy(
  startList: CounsellingRecord[],
  role: RoleContext
): CounsellingRecord[] {
  const { userTypeId: u, userType1: u1, isScert } = role;

  if (u === '10') {
    return startList.filter(
      (ftr) =>
        str(ftr.Location) === '1' ||
        str(ftr.Location) === '4' ||
        str(ftr.Location) === '5'
    );
  } else if (
    (u === '9' && u1 === '29') ||
    (u === '5' && u1 === '29') ||
    (u === '6' && u1 === '29')
  ) {
    return startList.filter((ftr) => str(ftr.Directorate) === '4');
  } else if (u === '5' && u1 === '8') {
    return startList.filter((ftr) => str(ftr.Directorate) === '6');
  } else if (isScert) {
    return startList.filter((ftr) => str(ftr.Directorate) === '5');
  }
  return startList.filter((ftr) => ftr.Location);
}

/** Empty path (conduct counselling) — operates on cnsnStartSts. */
function filterConductCounselling(
  startList: CounsellingRecord[],
  role: RoleContext
): CounsellingRecord[] {
  const { userTypeId: u, userType1: u1, isScert } = role;

  if (
    (u === '9' && u1 === '29') ||
    (u === '5' && u1 === '29') ||
    (u === '6' && u1 === '29')
  ) {
    return startList.filter((ftr) => str(ftr.Directorate) === '4');
  } else if (isScert) {
    return startList.filter((ftr) => str(ftr.Directorate) === '5');
  } else if (u === '5' && u1 === '8') {
    return startList.filter((ftr) => str(ftr.Directorate) === '6');
  }
  return startList.filter((ftr) => str(ftr.Directorate) !== '4');
}

/**
 * Top-level dispatcher — mirrors the `if (path === ...)` chain inside the
 * Angular `setTimeout` in `GetAllNewCounselling`.
 *
 * @param fullList  the mapped result list (SclPanel/Subject already split)
 * @param path      the current counselling path
 * @param role      the role context
 */
export function buildCounsellingList(
  fullList: CounsellingRecord[],
  path: CounsellingPath,
  role: RoleContext
): CounsellingRecord[] {
  const startList = startStatusList(fullList);

  if (path === 'add-candidate' || path === 'seniority') {
    return filterAddCandidateOrSeniority(fullList, role);
  } else if (path === 'update-vac') {
    return filterUpdateVac(fullList, role);
  } else if (path === 'vacancy' || path === 'candidate') {
    return filterVacancyOrCandidate(fullList, role);
  } else if (path === 'order-copy') {
    return filterOrderCopy(startList, role);
  } else if (path === 'counseling-reset') {
    return fullList;
  }
  // Empty path (conduct counselling)
  return filterConductCounselling(startList, role);
}

/** Derives isScert from role type ids (Angular constructor logic). */
export function deriveIsScert(userTypeId: string, userType1: string): boolean {
  return (
    (userTypeId === '5' && userType1 === '6') ||
    (userTypeId === '5' && userType1 === '7') ||
    userTypeId === '22'
  );
}

/* eslint-enable @typescript-eslint/no-explicit-any */
